// WeightConverter.jsx - Grams, pennyweight, troy ounces and pounds, all at once.
//
// The reason this page exists: a troy ounce is 31.1 grams and a kitchen ounce is
// 28.35 grams, and confusing the two overstates a lot by nearly ten percent. That
// mistake is behind a large share of the "your offer is too low" conversations in
// this industry, so it is worth its own page.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { useSpot } from "../lib/hooks.js";
import * as analytics from "../lib/analytics.js";
import { moneyExact, toGrams, UNITS, GRAMS_PER_OZT, GRAMS_PER_DWT, METAL_LABELS } from "../lib/format.js";
import { Panel, Input, Select } from "../ui/kit.jsx";
import { ArrowRight } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { CoinStack } from "../ui/illustrations.jsx";
import { PageHead, CtaBand, MobileCta, EmbedThis } from "./parts.jsx";

const AVOIRDUPOIS_OZ = 28.349523125;

const OUTPUTS = [
  { label: "Grams", divisor: 1, decimals: 2, note: "What a jeweller's scale reads" },
  { label: "Pennyweight (dwt)", divisor: GRAMS_PER_DWT, decimals: 3, note: "The trade unit in the United States" },
  { label: "Troy ounces (ozt)", divisor: GRAMS_PER_OZT, decimals: 4, note: "How metal is priced" },
  { label: "Standard ounces (oz)", divisor: AVOIRDUPOIS_OZ, decimals: 3, note: "Your kitchen scale, not used for metal" },
  { label: "Pounds", divisor: 453.59237, decimals: 4, note: "For large silver lots" },
];

export default function WeightConverter() {
  const { spot } = useSpot();
  const [weight, setWeight] = useState("10");
  const [unit, setUnit] = useState("g");

  const gramsValue = toGrams(weight, unit);

  // The field starts at 10 grams so the page is never empty, which means a page
  // view would otherwise look like a conversion. This fires on the first change
  // somebody actually makes.
  const used = useRef(false);
  useEffect(() => {
    if (used.current) return;
    used.current = true;
    analytics.trackToolUsed("weight_converter", { unit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight, unit]);

  return (
    <>
      <Seo
        title="Precious metal weight converter"
        path="/tools/weight-converter"
        description="Convert between grams, pennyweight, troy ounces and standard ounces. A troy ounce is 31.10 grams, not the 28.35 grams of a kitchen ounce."
        image={SITE.ogTools}
      />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Calculators", to: "/tools" },
        { label: "Weight converter", to: "/tools/weight-converter" },
      ])} />

      <PageHead
        eyebrow="Reference"
        title="A troy ounce is not an ounce."
        lede="It is 31.10 grams against 28.35, a difference of about nine percent. Precious metals are always priced by the troy ounce, and mixing the two up is the single most common arithmetic mistake people make before they sell."
        art={() => <CoinStack className="max-w-[13rem] ml-auto" />}
      />

      <div className="shell pb-14 md:pb-24">
        <div className="grid gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div>
            <Reveal as={Panel} className="p-5 sm:p-6">
              <div className="grid grid-cols-[1fr_130px] gap-3">
                <Input
                  label="Weight"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={weight}
                  onChange={(event) => setWeight(event.target.value)}
                  autoFocus
                />
                <Select
                  label="Unit"
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                  options={[...UNITS.map((entry) => ({ value: entry.value, label: entry.label })), { value: "oz", label: "standard oz" }]}
                />
              </div>

              <dl className="mt-6 divide-y divide-rule-soft border-t border-rule">
                {OUTPUTS.map((output) => {
                  const source = unit === "oz" ? Number(weight || 0) * AVOIRDUPOIS_OZ : gramsValue;
                  return (
                    <div key={output.label} className="flex items-baseline justify-between gap-4 py-3">
                      <dt>
                        <span className="block text-[14.5px] text-ink">{output.label}</span>
                        <span className="block text-[12.5px] text-faint">{output.note}</span>
                      </dt>
                      <dd className="num font-semibold text-ink text-right">
                        {(source / output.divisor).toFixed(output.decimals)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </Reveal>

            <Reveal as={Panel} delay={110} quiet className="p-5 mt-4">
              <p className="eyebrow mb-3">What that weight of pure metal is worth</p>
              <dl className="space-y-2 text-[14.5px]">
                {["gold", "silver", "platinum"].map((metal) => (
                  <div key={metal} className="flex justify-between">
                    <dt className="text-muted">{METAL_LABELS[metal]}</dt>
                    <dd className="num text-ink font-medium">
                      {spot?.perGram?.[metal] ? moneyExact((unit === "oz" ? Number(weight || 0) * AVOIRDUPOIS_OZ : gramsValue) * spot.perGram[metal]) : "—"}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="hint mt-3 pt-3 border-t border-rule leading-relaxed">
                Pure metal, at today&rsquo;s spot. Jewellery is an alloy, so use the{" "}
                <Link to="/tools/gold-calculator" className="link text-ink">calculator</Link> for a real piece.
              </p>
            </Reveal>
          </div>

          <StaggerBoxed as="div" step={110} className="space-y-8">
            {[
              ["Why two different ounces exist", "The troy system comes from the medieval fair at Troyes and survived because it was already the standard for coinage. The avoirdupois ounce on your kitchen scale came from the wool trade. Metals kept troy, everything else went avoirdupois, and the names never got fixed."],
              ["Pennyweight, and why the trade uses it", "One pennyweight is one twentieth of a troy ounce, 1.555 grams. It is the weight of an English silver penny from the twelfth century. American jewellers still price scrap by the pennyweight, so if you are quoted a rate per dwt, multiply by 20 to compare it to a per ounce price."],
              ["What a scale at home will tell you", "A kitchen scale reading in grams is fine, and grams are what we work in. Do not use its ounce setting. If your scale only reads to the nearest gram, weigh several pieces together and divide, or just send them and let our scale do it."],
            ].map(([title, body]) => (
              <div key={title}>
                <h2 className="display display-md text-ink">{title}</h2>
                <p className="hint mt-2.5 leading-relaxed">{body}</p>
              </div>
            ))}

            <div className="pt-2">
              <Link to="/get-a-kit" className="btn btn-primary">Get a free kit <ArrowRight size={16} /></Link>
            </div>
          </StaggerBoxed>
        </div>
      </div>

      <div className="shell pb-10">
        <EmbedThis tool="converter" />
      </div>

      <CtaBand />
      <MobileCta />
    </>
  );
}
